import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { TrinketSeo } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { TRINKET } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['ad'].trialCourseQuery}`

const Trinket = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...TrinketSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[TRINKET].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Top Coding Class Instructors for Kids and Teens to Learn Trinket"
        description="Trinket is like a magical playground where kids can bring their ideas to life through code. Using simple blocks and Python’s Turtle module, they’ll create drawings, fun animations, and even their own games—just like real programmers! Whether they’re designing a house, a caterpillar, or colorful shapes, kids will see their creations instantly come alive on screen. Trinket is completely browser-based and easy to use, making it perfect for beginners. As they explore, kids build a strong foundation in Python while developing creativity, logic, and problem-solving skills in a fun, visual, and interactive way."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={TRINKET}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={TRINKET} />
          <RecommendedCourses
            courseName={TRINKET}
            courseCodes={['ad']}
            title="Our Curriculum"
            description={
              <Trans>
                At Codingal, students get to explore the awesome world of Python
                programming in a super fun and exciting way! In our Codingal
                Grandmaster Course, we introduce kids to the Trinket module
                where they can create their own cool projects, starting with
                block-based coding.
                <br />
                They&#39;ll dive into fun things like:
                <ul>
                  <li>
                    Drawing a house and a scenic landscape using Python&#39;s
                    Turtle library 🏡🌳
                  </li>
                  <li>Making a caterpillar with random circles 🐛</li>
                  <li>Creating shapes of all kinds! 🔵🔺</li>
                </ul>
                Drawing a house and a scenic landscape using Python&#39;s Turtle
                library 🏡🌳 Making a caterpillar with random circles 🐛
                Creating shapes of all kinds! 🔵🔺
                <br />
                But that&#39;s not all! Along the way, kids will also get to
                learn important coding concepts like loops (for repeating tasks)
                and conditionals (to make decisions). Plus, they’ll practice
                typing code with fun, interactive activities, helping them get
                ready to jump into the world of text-based coding!
                <br />
                It&#39;s a hands-on adventure where kids can code their very own
                creations, one block at a time! 🚀
              </Trans>
            }
          />
          <Activities courseName={TRINKET} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked coding tutors
                ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                All our instructors have a background in Computer Science and
                undergo rigorous training, contributing to our outstanding NPS
                score of 86. Teachers receive a rating of 4.9 out of 5 from
                students and parents, while the curriculum content is rated 4.8
                out of 5. With a teacher ratio of 91% female to 9% male, we
                provide a comfortable learning environment. Our tutors
                understand students&#39; needs and offer personalized attention,
                even in group classes. Additionally, students have access to
                24/7 doubt resolution with an expert tutor whenever they need
                assistance.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={TRINKET}
            title={
              <Trans>An engaging Trinket programming class for kids</Trans>
            }
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create an ideal environment for kids to master
                  Trinket from the ground up. Our thoughtfully designed
                  curriculum combines real-world tools, creative projects, and
                  interactive learning to ensure a comprehensive and enjoyable
                  learning experience.
                </span>
              </Trans>
            }
            courseName={TRINKET}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                At Codingal, we are proud to be a STEM.org-accredited coding
                school, utilizing scientific teaching methods based on
                Bloom&#29;s Taxonomy, BIDE, and STEAM principles. Our curriculum
                is meticulously aligned with Common Core, state standards, and
                Advanced Placement (AP) curricula, ensuring students receive a
                comprehensive and relevant education. We also cover the K-12
                Computer Science Framework, providing developmentally
                appropriate content that equips learners with essential skills
                for a technology-driven world.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your STEM accredited certifications"
            description={
              <>
                In the Coding Grandmaster for Kids course, learners from grades
                1 to 3 can earn STEM.org-certified coding certificates—Python
                Programmer certificate—that celebrate their achievements. Using
                Trinket as the platform, students progress from building simple
                animations to creating their own cool, fully functional
                projects!
                <br />
                As they advance through the course, students unlock special
                badges and leaderboard rankings, making app development a fun
                and rewarding experience. These certifications are easy to
                download and share, are STEM.org accredited, and can be added to
                your child&#39;s learning portfolio to showcase their
                accomplishments to the world and on social media.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview shouldScrollToTop isCoursepage />
          <CodingForStudents
            title="Our Trinket Classes are Appropriate for Elementary School Students"
            description={
              <>
                Our Trinket classes are designed specifically for elementary
                school students, offering a clear and easy-to-follow course
                progression. We begin with fun, beginner-level concepts that
                introduce kids to the basics of coding in an engaging way. As
                they gain confidence, they move on to more advanced topics,
                gradually building a strong foundation in coding. This
                structured approach ensures students develop the skills needed
                to tackle more complex projects and programming languages as
                they continue their coding journey.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <Faq courseName={TRINKET} title="Frequently Asked Questions" />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses
            courseCodes={['aiepc', 'aigc', 'rc', 'sp', 'pc', 'cgm']}
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={TRINKET}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Trinket for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Trinket
